import type { FeatureCollection } from 'geojson';
import type { GeocodeResult, Meta, NearbyStop, Stations } from './types';

const BASE = '/api';

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { signal });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) detail = body.error;
    } catch {
      /* keep statusText */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export function fetchMeta(): Promise<Meta> {
  return getJson<Meta>('/meta');
}

export function fetchStations(): Promise<Stations> {
  return getJson<Stations>('/stations');
}

/**
 * Travel-time vector between a point and every station, as Uint16 seconds.
 * `dir: 'to'` = from each station to the point; `'from'` = the reverse.
 */
export async function fetchVector(
  lat: number,
  lon: number,
  dir: 'to' | 'from',
  signal?: AbortSignal
): Promise<ArrayBuffer> {
  const res = await fetch(`${BASE}/vector?lat=${lat}&lon=${lon}&dir=${dir}`, { signal });
  if (!res.ok) throw new Error(`could not load travel times (${res.status})`);
  return res.arrayBuffer();
}

export function geocode(q: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  return getJson<GeocodeResult[]>(`/geocode?q=${encodeURIComponent(q)}`, signal);
}

export function reverseGeocode(
  lat: number,
  lon: number,
  signal?: AbortSignal
): Promise<{ label?: string; detail?: string }> {
  return getJson(`/reverse?lat=${lat}&lon=${lon}`, signal);
}

export function fetchNearby(lat: number, lon: number, signal?: AbortSignal): Promise<NearbyStop[]> {
  return getJson<NearbyStop[]>(`/nearby?lat=${lat}&lon=${lon}`, signal);
}

/** S-Bahn line geometry for the navigation overlay. */
export function fetchLines(signal?: AbortSignal): Promise<FeatureCollection> {
  return getJson<FeatureCollection>('/lines', signal);
}
